import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Heart, 
  Users, 
  Trophy, 
  Target,
  MapPin,
  Sparkles,
  ArrowRight,
  Quote,
  Plane,
  Star
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_matchfinder-25/artifacts/wk49ezk2_Logo.jpg";
const FOUNDER_IMAGE = "https://customer-assets.emergentagent.com/job_3a696a4c-81d4-434e-a061-9984ef8c7b19/artifacts/jokgydak_Amir%20Belhadj.png";

export default function About() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Tunis Kick" className="h-10 w-10 rounded-full object-cover" />
              <span className="font-display font-bold text-xl text-tunis-blue">Tunis Kick</span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="font-semibold text-tunis-blue">
                  Anmelden
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary px-6">
                  Registrieren
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-28 pb-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-tunis-teal/10 rounded-full text-tunis-teal text-sm font-medium mb-5">
            <Sparkles className="w-4 h-4" />
            Die Geschichte hinter der Plattform
          </div>
          
          <h1 className="font-display font-bold text-4xl sm:text-5xl text-tunis-blue mb-5">
            Über <span className="text-tunis-teal">Tunis Kick</span>
          </h1>
          
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Die Idee zu Tunis Kick entstand aus meiner eigenen Leidenschaft für den Fußball 
            und meinen persönlichen Erfahrungen zwischen zwei Ländern.
          </p>
        </div>
      </section>

      {/* Founder Introduction */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-tunis-blue to-tunis-blue/90 rounded-3xl p-8 md:p-10 shadow-xl">
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Smaller Founder Image */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <div className="w-40 h-40 md:w-48 md:h-48 rounded-2xl overflow-hidden border-4 border-white/20 shadow-2xl">
                    <img 
                      src={FOUNDER_IMAGE}
                      alt="Amir Belhadj"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-white px-4 py-1.5 rounded-full shadow-lg">
                    <p className="text-sm font-bold text-tunis-blue whitespace-nowrap">Amir Belhadj</p>
                  </div>
                </div>
              </div>
              
              {/* Quote */}
              <div className="flex-1 text-center md:text-left">
                <Quote className="w-10 h-10 text-white/30 mb-4 mx-auto md:mx-0" />
                <p className="text-lg md:text-xl text-white leading-relaxed font-medium">
                  "Für mich war Fußball immer mehr als nur ein Hobby: Er war ein Ausgleich zum Alltag, 
                  eine Möglichkeit, Stress abzubauen, Zeit mit Freunden zu verbringen und sich für 
                  90 Minuten wie ein Profi zu fühlen."
                </p>
                <p className="mt-4 text-tunis-teal font-semibold">– Gründer von Tunis Kick</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Timeline */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            
            {/* My Story */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-red-500" />
              </div>
              <div className="flex-1 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <h2 className="font-display font-bold text-xl text-tunis-blue mb-3">Meine Geschichte</h2>
                <p className="text-slate-600 leading-relaxed">
                  Ich heiße <span className="font-semibold text-tunis-blue">Amir Belhadj</span>, bin in 
                  Deutschland geboren und aufgewachsen und habe dort selbst viele Jahre in der Amateur-Liga 
                  Fußball gespielt. Durch den Fußball habe ich früh gelernt, was 
                  <span className="font-semibold text-tunis-blue"> Disziplin</span>, 
                  <span className="font-semibold text-tunis-blue"> Teamgeist</span> und 
                  <span className="font-semibold text-tunis-blue"> Verlässlichkeit</span> bedeuten.
                </p>
              </div>
            </div>

            {/* The Challenge */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <MapPin className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1 bg-amber-50 border border-amber-200 rounded-2xl p-6">
                <h2 className="font-display font-bold text-xl text-tunis-blue mb-3">Die Herausforderung in Tunesien</h2>
                <p className="text-slate-600 leading-relaxed">
                  Als ich regelmäßig Zeit in <span className="font-semibold">Tunesien</span> verbracht habe, 
                  habe ich genau das vermisst: Eine organisierte Hobby- oder Amateur-Liga, feste Spielstrukturen, 
                  einfache Buchungen von Plätzen oder die Möglichkeit, über Fußball neue Bekanntschaften zu knüpfen.
                </p>
              </div>
            </div>

            {/* The Idea */}
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-tunis-teal/20 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-tunis-teal" />
              </div>
              <div className="flex-1 bg-gradient-to-br from-tunis-teal/10 to-tunis-teal/5 border border-tunis-teal/20 rounded-2xl p-6">
                <h2 className="font-display font-bold text-xl text-tunis-blue mb-3">Die Idee</h2>
                <p className="text-lg font-medium text-tunis-blue mb-2">
                  Warum das, was in Deutschland selbstverständlich ist, nicht auch in Tunesien möglich machen?
                </p>
                <p className="text-slate-600 leading-relaxed">
                  Mit <span className="font-semibold text-tunis-teal">Tunis Kick</span> schaffe ich eine Plattform, 
                  die Fußballbegeisterte zusammenbringt, Spiele organisiert und den Amateurfußball in Tunesien 
                  auf ein neues Level hebt – <span className="font-semibold">modern, digital und gemeinschaftlich</span>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sponsor Section */}
      <section className="py-16 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-100 rounded-full text-amber-700 text-sm font-medium mb-4">
              <Trophy className="w-4 h-4" />
              Unser Sponsor
            </div>
            <h2 className="font-display font-bold text-3xl text-tunis-blue">
              Unterstützt von einem Profi
            </h2>
          </div>
          
          <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
            <div className="grid md:grid-cols-2">
              {/* Sponsor Image */}
              <div className="relative bg-gradient-to-br from-tunis-blue to-tunis-blue/80 p-8 flex items-center justify-center">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-10 left-10 w-32 h-32 border-4 border-white rounded-full" />
                  <div className="absolute bottom-10 right-10 w-24 h-24 border-4 border-white rounded-full" />
                </div>
                <div className="relative">
                  <div className="w-56 h-72 md:w-64 md:h-80 rounded-2xl overflow-hidden border-4 border-white/30 shadow-2xl">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_3a696a4c-81d4-434e-a061-9984ef8c7b19/artifacts/2zyz6w2s_Anis%20ben%20hatira.jpeg"
                      alt="Anis Ben Hatira"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-white px-5 py-2 rounded-full shadow-lg">
                    <p className="font-bold text-tunis-blue whitespace-nowrap">⚽ Anis Ben Hatira</p>
                  </div>
                </div>
              </div>
              
              {/* Sponsor Info */}
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-tunis-teal/10 rounded-full flex items-center justify-center">
                    <span className="text-xl">⚽</span>
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-2xl text-tunis-blue">Anis Ben Hatira</h3>
                    <p className="text-slate-500">Profifußballer</p>
                  </div>
                </div>
                
                <div className="space-y-4 text-slate-600 leading-relaxed">
                  <p>
                    <span className="font-semibold text-tunis-blue">Anis Ben Hatira</span>, ehemaliger Bundesliga-Profi, 
                    unterstützt Tunis Kick als offizieller Sponsor.
                  </p>
                  <p>
                    Als tunesischstämmiger Fußballstar möchte er den Menschen in Tunesien die Möglichkeit geben, 
                    ihre <span className="font-semibold text-tunis-teal">Leidenschaft für den Fußball</span> nicht nur 
                    im Traum, sondern auch in der Wirklichkeit auszuleben.
                  </p>
                  <p className="italic text-tunis-blue font-medium border-l-4 border-tunis-teal pl-4">
                    "Jeder, der Fußball liebt, verdient schöne Momente auf dem Platz – egal wo auf der Welt."
                  </p>
                </div>
                
                <div className="mt-6 flex items-center gap-3">
                  <span className="text-2xl">🇹🇳</span>
                  <span className="text-2xl">🇩🇪</span>
                  <span className="text-sm text-slate-500">Verbindet zwei Länder durch Fußball</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 mb-3">
              <Star className="w-5 h-5 text-amber-500" />
              <span className="text-sm font-semibold text-tunis-blue uppercase tracking-wide">Unsere Werte</span>
              <Star className="w-5 h-5 text-amber-500" />
            </div>
            <h2 className="font-display font-bold text-3xl text-tunis-blue">
              Was uns antreibt
            </h2>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Heart, title: 'Leidenschaft', desc: 'Für den schönsten Sport der Welt', color: 'red' },
              { icon: Trophy, title: 'Fairplay', desc: 'Respekt auf und neben dem Platz', color: 'green' },
              { icon: Target, title: 'Organisation', desc: 'Struktur für besseren Fußball', color: 'blue' },
              { icon: Users, title: 'Gemeinschaft', desc: 'Zusammen sind wir stärker', color: 'purple' },
            ].map((value, index) => (
              <div 
                key={index}
                className="bg-white p-5 rounded-2xl border border-slate-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 text-center"
              >
                <div className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center
                  ${value.color === 'red' ? 'bg-red-100' : ''}
                  ${value.color === 'green' ? 'bg-green-100' : ''}
                  ${value.color === 'blue' ? 'bg-blue-100' : ''}
                  ${value.color === 'purple' ? 'bg-purple-100' : ''}
                `}>
                  <value.icon className={`w-6 h-6
                    ${value.color === 'red' ? 'text-red-500' : ''}
                    ${value.color === 'green' ? 'text-green-500' : ''}
                    ${value.color === 'blue' ? 'text-blue-500' : ''}
                    ${value.color === 'purple' ? 'text-purple-500' : ''}
                  `} />
                </div>
                <h3 className="font-bold text-tunis-blue mb-1">{value.title}</h3>
                <p className="text-sm text-slate-500">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-gradient-to-r from-tunis-blue via-tunis-blue to-tunis-teal rounded-3xl p-10 text-center overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-tunis-teal/30 rounded-full blur-2xl" />
            
            <div className="relative">
              {/* Flags */}
              <div className="flex justify-center items-center gap-3 mb-6">
                <span className="text-3xl">🇩🇪</span>
                <Plane className="w-5 h-5 text-white/60" />
                <span className="text-3xl">🇹🇳</span>
              </div>
              
              <h2 className="font-display font-bold text-2xl sm:text-3xl text-white mb-3">
                Für alle, die Fußball lieben
              </h2>
              <p className="text-white/80 mb-6 max-w-lg mx-auto">
                Egal ob Hobbyspieler oder ambitionierter Amateur – werde Teil der Tunis Kick Community!
              </p>
              
              <Link to="/register">
                <Button size="lg" className="bg-white text-tunis-blue hover:bg-white/90 font-semibold px-8 shadow-lg">
                  Jetzt mitmachen
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              
              <div className="mt-6 text-3xl">⚽</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Tunis Kick" className="h-8 w-8 rounded-full object-cover" />
              <span className="font-display font-bold text-tunis-blue">Tunis Kick</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <Link to="/" className="hover:text-tunis-blue transition-colors">Startseite</Link>
              <span className="text-tunis-teal font-medium">Über uns</span>
              <span>© 2024</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
